import React, { Component } from "react";
import { graphql } from "react-apollo";
import gql from "graphql-tag";
import { MathJax, MathJaxContext } from "better-react-mathjax";

/* ============================================================================
   IIT Foundation Theory — the textbook theory from foundation.theory.

   getIITFoundationTheoryOutline(class_id) gives subjects → chapters → topics →
   section ids without any content; the sidebar is built from it. Opening a
   topic asks getIITFoundationTheorySections for just that topic's sections, so
   no request ever carries a whole book.

   The content is Mathpix output: inline LaTeX ($…$), markdown images in table
   cells, and the odd inline <img>. It is never injected as HTML — text stays a
   React text node that MathJax typesets, and images are pulled out into real
   <img> elements with their src checked.
   ========================================================================== */

const GET_THEORY_OUTLINE = gql`
  query ($class_id: Int, $class: String) {
    getIITFoundationTheoryOutline(class_id: $class_id, class: $class) {
      subject
      book_name
      chapters {
        id
        chapter_name
        topics {
          id
          topic_name
          sections {
            id
            subtopic_name
          }
        }
      }
    }
  }
`;

const GET_THEORY_SECTIONS = gql`
  query ($ids: [Int!]!) {
    getIITFoundationTheorySections(ids: $ids) {
      id
      subtopic_name
      blocks {
        type
        text
        title
        items
        src
        caption
        columns
        rows
      }
    }
  }
`;

const MATHJAX_CONFIG = {
  loader: { load: ["input/tex", "output/chtml"] },
  tex: {
    inlineMath: [["$", "$"], ["\\(", "\\)"]],
    displayMath: [["$$", "$$"], ["\\[", "\\]"]],
    processEscapes: true,
  },
  options: { enableMenu: false },
};

const SUBJECT_ICON = {
  Physics: "fas fa-atom",
  Chemistry: "fas fa-flask",
  Mathematics: "fas fa-square-root-alt",
  Biology: "fas fa-dna",
  Science: "fas fa-microscope",
};

/* ------------------------------------------------------------ rich text */

// Markdown image ![](url) or an inline <img … src="…">.
const IMAGE_RE = /!\[[^\]]*\]\(([^)\s]+)\)|<img\b[^>]*?\bsrc\s*=\s*["']([^"']+)["'][^>]*>/gi;
// Math spans, which must reach MathJax untouched.
const MATH_RE = /(\$\$[\s\S]+?\$\$|\$[^$]+?\$|\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\])/;

// Only images from the web or embedded image data - never javascript: etc.
const safeSrc = (src) => (/^(https?:\/\/|data:image\/)/i.test(src || "") ? src : null);

// Mathpix escapes &, %, # and _ outside math. \$ is left for MathJax
// (processEscapes) so it isn't mistaken for a math delimiter.
const unescapeText = (value) =>
  value
    .split(MATH_RE)
    .map((part, index) => (index % 2 === 1 ? part : part.replace(/\\([&%#_])/g, "$1")))
    .join("");

// Plain label text (chapter / topic names) - no math rendering, just escapes.
const cleanLabel = (value) => (value || "").replace(/\\([&%#_$])/g, "$1");

const RichText = ({ text }) => {
  if (!text) {
    return null;
  }

  const parts = [];
  let last = 0;
  let match;
  IMAGE_RE.lastIndex = 0;

  while ((match = IMAGE_RE.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(unescapeText(text.slice(last, match.index)));
    }
    const src = safeSrc(match[1] || match[2]);
    if (src) {
      parts.push(<img key={match.index} className="thx-inline-img" src={src} alt="" loading="lazy" />);
    }
    last = IMAGE_RE.lastIndex;
  }
  if (last < text.length) {
    parts.push(unescapeText(text.slice(last)));
  }

  return <>{parts}</>;
};

/* --------------------------------------------------------------- blocks */

const Block = ({ block }) => {
  switch (block.type) {
    case "paragraph":
      return (
        <p className="thx-p">
          <RichText text={block.text} />
        </p>
      );
    case "list":
      return (
        <div className="thx-list">
          {block.title ? (
            <h6>
              <RichText text={block.title} />
            </h6>
          ) : null}
          <ul>
            {(block.items || []).map((item, index) => (
              <li key={index}>
                <RichText text={item} />
              </li>
            ))}
          </ul>
        </div>
      );
    case "image": {
      const src = safeSrc(block.src);
      return !src ? null : (
        <figure className="thx-figure">
          <img src={src} alt={block.caption || ""} loading="lazy" />
          {block.caption ? (
            <figcaption>
              <RichText text={block.caption} />
            </figcaption>
          ) : null}
        </figure>
      );
    }
    case "table":
      return (
        <div className="thx-table">
          {block.title ? (
            <h6>
              <RichText text={block.title} />
            </h6>
          ) : null}
          <div className="thx-table__scroll">
            <table>
              {block.columns && block.columns.length ? (
                <thead>
                  <tr>
                    {block.columns.map((column, index) => (
                      <th key={index}>
                        <RichText text={column} />
                      </th>
                    ))}
                  </tr>
                </thead>
              ) : null}
              <tbody>
                {(block.rows || []).map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex}>
                        <RichText text={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    default:
      return null;
  }
};

/* --------------------------------------------------------------- styles */
const STYLES = `
/* Built on the foundation design system (theme/_fdn-theme.scss): the page
   colour is teal, subject UI follows --subj, everything else uses --fdn-* tokens. */
.thx { --thx-accent:var(--fdn-teal); --thx-accent-dark:var(--fdn-teal-dark); --thx-accent-soft:var(--fdn-teal-soft);
  max-width:1360px; margin:0 auto; min-height:100%; color:var(--fdn-ink); font-size:15px; }
.thx h6 { font-family:var(--fdn-display); letter-spacing:-.1px; }

/* header (fdn-hero fdn-hero--teal) - subject tabs sit on the gradient */
.thx-subjects { display:flex; gap:10px; flex-wrap:wrap; }
.thx-subject { display:inline-flex; align-items:center; gap:9px; padding:6px 18px 6px 6px; border-radius:var(--fdn-radius-pill);
  border:2px solid rgba(255,255,255,.4); background:rgba(255,255,255,.16); color:#fff;
  font-size:14.5px; font-weight:800; cursor:pointer; transition:all .2s var(--fdn-ease); }
.thx-subject i { display:flex; align-items:center; justify-content:center; width:32px; height:32px; border-radius:50%;
  font-size:14px; color:var(--subj-dark); background:#fff; transition:transform .35s var(--fdn-bounce); }
.thx-subject:focus { outline:none; }
.thx-subject:focus-visible { box-shadow:0 0 0 3px rgba(255,255,255,.6); }
.thx-subject:hover { background:rgba(255,255,255,.28); transform:translateY(-1px); }
.thx-subject:hover i { transform:rotate(-10deg) scale(1.06); }
.thx-subject.is-active { background:#fff; color:var(--subj-dark); border-color:#fff;
  box-shadow:0 5px 0 var(--subj-dark); }
.thx-subject.is-active i { color:#fff; background:var(--subj); }

.thx-grid { display:grid; grid-template-columns:310px minmax(0,1fr); gap:22px; align-items:start; }

/* sidebar - chapter / topic navigator */
.thx-side { position:sticky; top:16px; max-height:calc(100vh - 32px); overflow-y:auto; padding:18px 14px;
  border-radius:var(--fdn-radius-lg); background:var(--fdn-surface); border:1.5px solid var(--fdn-line);
  box-shadow:var(--fdn-shadow-sm); }
.thx-side::-webkit-scrollbar { width:6px; }
.thx-side::-webkit-scrollbar-thumb { background:var(--fdn-line-strong); border-radius:6px; }
.thx-side__title { display:flex; align-items:center; gap:9px; margin:0 4px 14px; font-size:16px; font-weight:600;
  color:var(--fdn-ink); }
.thx-side__title i { display:flex; align-items:center; justify-content:center; width:30px; height:30px;
  border-radius:10px; font-size:13px; color:var(--subj-dark); background:var(--subj-soft); }
.thx-search { position:relative; margin-bottom:14px; }
.thx-search i { position:absolute; left:14px; top:50%; transform:translateY(-50%); font-size:13px;
  color:var(--fdn-muted); pointer-events:none; }
.thx-search input { width:100%; height:44px; padding:0 14px 0 38px; border:2px solid var(--fdn-line);
  border-radius:var(--fdn-radius-sm); background:var(--fdn-bg-alt); font-size:14px; font-weight:700;
  color:var(--fdn-ink); transition:border-color .2s ease, box-shadow .2s ease, background .2s ease; }
.thx-search input::placeholder { color:var(--fdn-muted); font-weight:600; }
.thx-search input:focus { outline:none; border-color:var(--thx-accent); background:var(--fdn-surface);
  box-shadow:0 0 0 4px var(--thx-accent-soft); }
.thx-chapter { margin-bottom:10px; border-radius:var(--fdn-radius); border:2px solid var(--fdn-line);
  background:var(--fdn-surface); transition:border-color .2s ease, box-shadow .2s ease; }
.thx-chapter.is-open { border-color:var(--thx-accent-soft); box-shadow:0 8px 18px -10px var(--thx-accent); }
.thx-chapter__btn { display:flex; align-items:center; gap:10px; width:100%; padding:11px 12px; border:0;
  border-radius:14px; background:transparent; font-size:14.5px; font-weight:800; line-height:1.3;
  color:var(--fdn-ink); cursor:pointer; text-align:left; transition:background .2s ease, color .2s ease; }
.thx-chapter__btn:focus { outline:none; }
.thx-chapter__btn:focus-visible { box-shadow:0 0 0 3px var(--thx-accent-soft); }
.thx-chapter__btn:hover { background:var(--fdn-bg); }
.thx-chapter.is-open .thx-chapter__btn { background:var(--thx-accent-soft); color:var(--thx-accent-dark);
  border-radius:14px 14px 0 0; }
.thx-chapter__btn .num { display:flex; align-items:center; justify-content:center; width:32px; height:32px;
  border-radius:11px; font-family:var(--fdn-display); font-size:15px; font-weight:700; color:var(--thx-accent-dark);
  background:var(--thx-accent-soft); flex:0 0 auto; transition:transform .35s var(--fdn-bounce), background .2s ease, color .2s ease; }
.thx-chapter__btn:hover .num { transform:rotate(-8deg) scale(1.06); }
.thx-chapter.is-open .thx-chapter__btn .num { color:#fff; background:var(--thx-accent); }
.thx-chapter__btn .caret { margin-left:auto; font-size:11px; color:var(--fdn-muted); }
.thx-topics { padding:8px; }
.thx-topic { display:flex; align-items:center; gap:10px; width:100%; padding:10px 12px; margin-bottom:4px; border:0;
  border-radius:var(--fdn-radius-sm); background:transparent; font-size:14px; font-weight:700; line-height:1.35;
  color:var(--fdn-body); text-align:left; cursor:pointer; transition:all .2s var(--fdn-ease); }
.thx-topic:last-child { margin-bottom:0; }
.thx-topic:before { content:""; width:8px; height:8px; border-radius:50%; flex:0 0 auto;
  background:var(--fdn-line-strong); transition:background .2s ease; }
.thx-topic:focus { outline:none; }
.thx-topic:focus-visible { box-shadow:0 0 0 3px var(--thx-accent-soft); }
.thx-topic:hover { background:var(--fdn-bg); color:var(--thx-accent-dark); transform:translateX(3px); }
.thx-topic:hover:before { background:var(--thx-accent); }
.thx-topic.is-active { color:#fff; background:var(--thx-accent); box-shadow:0 4px 0 var(--thx-accent-dark); }
.thx-topic.is-active:hover { transform:none; }
.thx-topic.is-active:before { background:#fff; }

/* reading area */
.thx-main { min-width:0; }
.thx-hero { position:relative; overflow:hidden; padding:24px 26px; margin-bottom:18px; border-radius:var(--fdn-radius-lg);
  background:var(--subj-soft); border:2px solid var(--fdn-surface); box-shadow:var(--fdn-shadow-sm); }
.thx-hero:after { content:""; position:absolute; width:150px; height:150px; right:-40px; top:-60px; border-radius:50%;
  background:var(--subj); opacity:.12; pointer-events:none; }
.thx-hero > * { position:relative; z-index:1; }
.thx-hero__crumb { margin-bottom:8px; font-size:13px; font-weight:800; color:var(--subj-dark); }
.thx-hero h2 { margin:0; font-size:28px; font-weight:700; line-height:1.2; color:var(--fdn-ink); }

.thx-card { padding:26px 30px; margin-bottom:18px; border-radius:var(--fdn-radius-lg); background:var(--fdn-surface);
  border:1.5px solid var(--fdn-line); box-shadow:var(--fdn-shadow-sm); }
.thx-card h3 { display:flex; align-items:center; gap:10px; margin:0 0 16px; font-size:21px; font-weight:600;
  line-height:1.3; color:var(--thx-accent-dark); }
.thx-card h3:before { content:""; width:6px; height:24px; border-radius:var(--fdn-radius-pill); flex:0 0 auto;
  background:var(--thx-accent); }
.thx-p { max-width:75ch; margin:0 0 16px; font-size:16px; line-height:1.8; color:var(--fdn-body); white-space:pre-line; }
.thx-list { max-width:75ch; margin:0 0 18px; padding:16px 20px; border-radius:var(--fdn-radius); background:var(--fdn-bg-alt);
  border:1.5px solid var(--fdn-line); }
.thx-list h6, .thx-table h6 { margin:0 0 10px; font-size:16.5px; font-weight:600; color:var(--fdn-ink); }
.thx-list ul { margin:0; padding-left:22px; }
.thx-list li { margin-bottom:8px; font-size:16px; line-height:1.75; color:var(--fdn-body); }
.thx-list li:last-child { margin-bottom:0; }
.thx-list li::marker { color:var(--thx-accent); }
.thx-figure { margin:6px 0 20px; text-align:center; }
.thx-figure img { max-width:100%; max-height:420px; padding:8px; border-radius:var(--fdn-radius);
  background:var(--fdn-surface); border:1.5px solid var(--fdn-line); }
.thx-figure figcaption { margin-top:8px; font-size:14px; font-weight:600; color:var(--fdn-muted); }
.thx-inline-img { max-width:100%; vertical-align:middle; }
.thx-table { margin:0 0 20px; }
.thx-table__scroll { overflow-x:auto; border:2px solid var(--fdn-line); border-radius:var(--fdn-radius); }
.thx-table table { width:100%; border-collapse:collapse; font-size:15px; }
.thx-table th { padding:12px 14px; background:var(--thx-accent-soft); color:var(--thx-accent-dark); font-weight:800;
  text-align:left; border-bottom:2px solid var(--fdn-line); }
.thx-table td { padding:11px 14px; border-bottom:1.5px solid var(--fdn-line); color:var(--fdn-body); line-height:1.6;
  vertical-align:top; }
.thx-table tbody tr:nth-child(even) td { background:var(--fdn-bg-alt); }
.thx-table tr:last-child td { border-bottom:0; }
.thx-table td .thx-inline-img { max-height:180px; }
.thx .MathJax { font-size:105%!important; }
mjx-container[display="true"] { overflow-x:auto; overflow-y:hidden; }

/* prev / next - chunky buttons */
.thx-nav { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px; margin-top:4px; }
.thx-navbtn { display:flex; align-items:center; gap:14px; padding:16px 18px; border-radius:var(--fdn-radius);
  background:var(--fdn-surface); border:2px solid var(--fdn-line); box-shadow:0 5px 0 var(--fdn-line-strong);
  cursor:pointer; text-align:left; transition:transform .12s ease, box-shadow .12s ease, border-color .2s ease; }
.thx-navbtn:focus { outline:none; }
.thx-navbtn:focus-visible { border-color:var(--thx-accent); }
.thx-navbtn:disabled { opacity:.5; cursor:not-allowed; box-shadow:none; }
.thx-navbtn:not(:disabled):hover { border-color:var(--thx-accent); transform:translateY(-2px);
  box-shadow:0 7px 0 var(--thx-accent-soft); }
.thx-navbtn:not(:disabled):active { transform:translateY(4px); box-shadow:0 1px 0 var(--fdn-line-strong); }
.thx-navbtn small { display:block; font-size:12px; font-weight:800; letter-spacing:.3px; text-transform:uppercase;
  color:var(--thx-accent-dark); }
.thx-navbtn strong { display:block; font-family:var(--fdn-display); font-size:16px; font-weight:600; line-height:1.3;
  color:var(--fdn-ink); }
.thx-navbtn .ic { display:flex; align-items:center; justify-content:center; width:44px; height:44px;
  border-radius:14px; font-size:16px; color:#fff; background:var(--thx-accent); box-shadow:0 4px 0 var(--thx-accent-dark);
  flex:0 0 auto; transition:transform .3s var(--fdn-bounce); }
.thx-navbtn:not(:disabled):hover .ic { transform:scale(1.08); }
.thx-navbtn:disabled .ic { background:var(--fdn-muted); box-shadow:none; }
.thx-navbtn.next { justify-content:flex-end; text-align:right; }

/* skeleton + states */
.thx-card .fdn-skel:first-child { height:18px; margin-bottom:16px; }
.thx-card .fdn-skel:last-child { margin-bottom:0; }
.thx-card.fdn-empty .fdn-empty__icon { color:var(--thx-accent-dark); background:var(--thx-accent-soft); }
.thx-card.fdn-empty p, .thx-card.fdn-error p { max-width:46ch; margin:0 auto; font-size:15px; line-height:1.6; }
.thx-card.fdn-error p { color:var(--fdn-body); }

@media (max-width:991px){
  .thx-grid { grid-template-columns:1fr; gap:16px; }
  .thx-side { position:static; max-height:none; }
}
@media (max-width:767px){
  .thx-subject { font-size:13.5px; padding-right:14px; }
  .thx-hero { padding:20px 18px; }
  .thx-hero h2 { font-size:22px; }
  .thx-card { padding:20px 16px; }
  .thx-card h3 { font-size:19px; }
  .thx-list { padding:14px 14px; }
  .thx-nav { grid-template-columns:1fr; }
}
@media (prefers-reduced-motion:reduce){
  .thx *,.thx *:before,.thx *:after { animation-duration:.001s!important; transition-duration:.001s!important; }
}
`;

/* -------------------------------------------------------------- pieces */

const Skeleton = () => (
  <div className="thx-card">
    <span className="fdn-skel" style={{ width: "40%" }} />
    <span className="fdn-skel" style={{ width: "90%" }} />
    <span className="fdn-skel" style={{ width: "80%" }} />
    <span className="fdn-skel" style={{ width: "60%" }} />
  </div>
);

// Presentation only: subject colour class from theme/_fdn-theme.scss.
const subjectClass = (name) => `fdn-subj--${String(name || "").toLowerCase()}`;

const State = ({ icon, title, children, error }) => (
  <div className={`thx-card ${error ? "fdn-error" : "fdn-empty"}`}>
    <div className={error ? "fdn-error__icon" : "fdn-empty__icon"}>
      <i className={icon} />
    </div>
    <h4>{title || (error ? "Oops, something went wrong" : "Nothing here yet")}</h4>
    <p>{children}</p>
  </div>
);

// Loads one topic's sections. Keyed by topic id from the parent, so switching
// topic remounts it and MathJax typesets the fresh content.
const TopicContentView = ({ data }) => {
  if (data.loading && !data.getIITFoundationTheorySections) {
    return <Skeleton />;
  }
  if (data.error) {
    return (
      <State icon="fas fa-exclamation-triangle" error>
        Couldn't load this topic. Please try again.
      </State>
    );
  }

  const sections = data.getIITFoundationTheorySections || [];
  if (!sections.length) {
    return <State icon="far fa-file-alt">No content for this topic yet.</State>;
  }

  return (
    <MathJax dynamic hideUntilTypeset="first">
      {sections.map((section) => (
        <div className="thx-card" key={section.id}>
          {section.subtopic_name ? <h3>{cleanLabel(section.subtopic_name)}</h3> : null}
          {(section.blocks || []).map((block, index) => (
            <Block block={block} key={index} />
          ))}
        </div>
      ))}
    </MathJax>
  );
};

const TopicContent = graphql(GET_THEORY_SECTIONS, {
  options: (props) => ({ variables: { ids: props.ids }, fetchPolicy: "cache-first" }),
})(TopicContentView);

/* ---------------------------------------------------------------- view */

class TheoryView extends Component {
  state = { subjectIndex: 0, chapterId: null, topicId: null, openChapterId: null, search: "" };

  componentDidUpdate(prevProps) {
    // Land on the first topic once the outline arrives.
    if (!prevProps.outline.getIITFoundationTheoryOutline && this.props.outline.getIITFoundationTheoryOutline) {
      this.selectSubject(0);
    }
  }

  componentDidMount() {
    if (this.props.outline.getIITFoundationTheoryOutline) {
      this.selectSubject(0);
    }
  }

  subjects = () => this.props.outline.getIITFoundationTheoryOutline || [];

  selectSubject = (subjectIndex) => {
    const subject = this.subjects()[subjectIndex];
    const chapter = subject && subject.chapters[0];
    const topic = chapter && chapter.topics[0];
    this.setState({
      subjectIndex,
      chapterId: chapter ? chapter.id : null,
      openChapterId: chapter ? chapter.id : null,
      topicId: topic ? topic.id : null,
      search: "",
    });
  };

  selectTopic = (chapterId, topicId) => {
    this.setState({ chapterId, topicId, openChapterId: chapterId });
    if (typeof window !== "undefined" && window.scrollTo) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  toggleChapter = (chapterId) =>
    this.setState((state) => ({ openChapterId: state.openChapterId === chapterId ? null : chapterId }));

  render() {
    const { outline } = this.props;
    const subjects = this.subjects();
    const subject = subjects[this.state.subjectIndex] || null;
    const chapters = subject ? subject.chapters : [];

    // Flat topic list in reading order, for prev/next and the selected lookup.
    const flat = [];
    chapters.forEach((chapter, chapterIndex) =>
      chapter.topics.forEach((topic) => flat.push({ chapter, chapterIndex, topic }))
    );
    const position = flat.findIndex((entry) => entry.topic.id === this.state.topicId);
    const current = position >= 0 ? flat[position] : null;
    const prev = position > 0 ? flat[position - 1] : null;
    const next = position >= 0 && position < flat.length - 1 ? flat[position + 1] : null;

    const query = this.state.search.trim().toLowerCase();
    const matches = (value) => cleanLabel(value).toLowerCase().indexOf(query) !== -1;
    const topicCount = chapters.reduce((total, chapter) => total + chapter.topics.length, 0);

    return (
      <div className="thx">
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />

        <div className="fdn-hero fdn-hero--teal thx-head">
          <div className="fdn-hero__row">
            <div className="fdn-hero__icon">
              <i className="fas fa-book-reader" />
            </div>
            <div>
              <h1 className="fdn-hero__title">Theory</h1>
              <p className="fdn-hero__sub">
                {subject ? cleanLabel(subject.book_name) : "Read the full textbook theory, chapter by chapter"}
              </p>
            </div>
            {subject ? (
              <div className="fdn-hero__stats">
                <div className="fdn-hero__stat">
                  <strong>{chapters.length}</strong>
                  <small>Chapters</small>
                </div>
                <div className="fdn-hero__stat">
                  <strong>{topicCount}</strong>
                  <small>Topics</small>
                </div>
              </div>
            ) : null}
          </div>
          {subjects.length > 1 ? (
            <div className="fdn-hero__body thx-subjects" role="tablist">
              {subjects.map((item, index) => (
                <button
                  key={`${item.subject}-${item.book_name}`}
                  type="button"
                  role="tab"
                  aria-selected={index === this.state.subjectIndex}
                  className={`thx-subject ${subjectClass(item.subject)}${
                    index === this.state.subjectIndex ? " is-active" : ""
                  }`}
                  onClick={() => this.selectSubject(index)}
                >
                  <i className={SUBJECT_ICON[item.subject] || "fas fa-book"} />
                  {item.subject}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {outline.loading && !subjects.length ? (
          <Skeleton />
        ) : outline.error ? (
          <State icon="fas fa-exclamation-triangle" error>
            Couldn't load theory. Please refresh the page.
          </State>
        ) : !subjects.length ? (
          <State icon="far fa-folder-open">No theory is available for your class yet.</State>
        ) : (
          <div className={`thx-grid ${subjectClass(subject ? subject.subject : "")}`}>
            <aside className="thx-side">
              <h6 className="thx-side__title">
                <i className={(subject && SUBJECT_ICON[subject.subject]) || "fas fa-book"} />
                Chapters &amp; Topics
              </h6>
              <div className="thx-search">
                <i className="fas fa-search" />
                <input
                  type="search"
                  placeholder="Search chapters or topics"
                  value={this.state.search}
                  onChange={(event) => this.setState({ search: event.target.value })}
                />
              </div>
              {chapters.map((chapter, chapterIndex) => {
                const chapterHit = !query || matches(chapter.chapter_name);
                const topics = chapterHit ? chapter.topics : chapter.topics.filter((t) => matches(t.topic_name));
                if (!topics.length) {
                  return null;
                }
                const open = query ? true : this.state.openChapterId === chapter.id;
                return (
                  <div className={`thx-chapter${open ? " is-open" : ""}`} key={chapter.id}>
                    <button
                      type="button"
                      className="thx-chapter__btn"
                      aria-expanded={open}
                      onClick={() => this.toggleChapter(chapter.id)}
                    >
                      {/* Numbered by position: the source chapter_number has gaps. */}
                      <span className="num">{chapterIndex + 1}</span>
                      <span>{cleanLabel(chapter.chapter_name)}</span>
                      <i className={`fas fa-chevron-${open ? "up" : "down"} caret`} />
                    </button>
                    {open ? (
                      <div className="thx-topics">
                        {topics.map((topic) => (
                          <button
                            type="button"
                            key={topic.id}
                            className={`thx-topic${topic.id === this.state.topicId ? " is-active" : ""}`}
                            onClick={() => this.selectTopic(chapter.id, topic.id)}
                          >
                            {cleanLabel(topic.topic_name)}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </aside>

            <div className="thx-main">
              {current ? (
                <>
                  <div className="thx-hero">
                    <div className="thx-hero__crumb">
                      {subject.subject} › Chapter {current.chapterIndex + 1} · {cleanLabel(current.chapter.chapter_name)}
                    </div>
                    <h2>{cleanLabel(current.topic.topic_name)}</h2>
                  </div>

                  <TopicContent
                    key={current.topic.id}
                    ids={current.topic.sections.map((section) => section.id)}
                  />

                  <div className="thx-nav">
                    <button
                      type="button"
                      className="thx-navbtn"
                      disabled={!prev}
                      onClick={() => prev && this.selectTopic(prev.chapter.id, prev.topic.id)}
                    >
                      <span className="ic">
                        <i className="fas fa-arrow-left" />
                      </span>
                      <span>
                        <small>Previous</small>
                        <strong>{prev ? cleanLabel(prev.topic.topic_name) : "—"}</strong>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="thx-navbtn next"
                      disabled={!next}
                      onClick={() => next && this.selectTopic(next.chapter.id, next.topic.id)}
                    >
                      <span>
                        <small>Next</small>
                        <strong>{next ? cleanLabel(next.topic.topic_name) : "—"}</strong>
                      </span>
                      <span className="ic">
                        <i className="fas fa-arrow-right" />
                      </span>
                    </button>
                  </div>
                </>
              ) : (
                <State icon="far fa-hand-point-left" title="Ready when you are">
                  Pick a topic to start reading.
                </State>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

const TheoryData = graphql(GET_THEORY_OUTLINE, {
  name: "outline",
  options: (props) => ({
    variables: props.classId ? { class_id: Number(props.classId) } : { class: props.classValue },
    fetchPolicy: "cache-and-network",
  }),
})(TheoryView);

// Owns the MathJax context so it is created once per page, not per topic.
const TheoryPage = (props) => (
  <MathJaxContext version={3} config={MATHJAX_CONFIG}>
    <TheoryData {...props} />
  </MathJaxContext>
);

export default TheoryPage;
